import React, { useEffect, useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../contexts/AuthContext";
import Notification from "../../components/UI/Notification";
import Logo from '../../assets/images/Imperative_Logo.png';
import Logo_white from '../../assets/images/imperative_white.png';
import IllustrationPanel from "../../components/UI/IllustrationPanel";
import axios from "axios";
import { getToken } from "../../utils/tokenUtils";

const Login = () => {
  const { login, roles, loading } = useContext(AuthContext); // Access login function and loading state
  const [formData, setFormData] = useState({ email: "", password: "" });
  const [error, setError] = useState(""); // State for error messages
  const [message, setMessage] = useState("");
  const [loadingLogin, setLoadingLogin] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const [notification, setNotification] = useState(null);
  const [justLoggedIn, setJustLoggedIn] = useState(false);
  const [theme, setTheme] = useState(localStorage.getItem("theme"));
  
  const [isForgotPasswordOpen, setIsForgotPasswordOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const token = getToken(); 

  const openForgotPasswordModal = () => {
    setIsForgotPasswordOpen(true);
  };

  const closeForgotPasswordModal = () => {
    setIsForgotPasswordOpen(false);
    setEmail(''); // Reset email input
  };

  useEffect(() => {
    const handleStorageChange = () => {
      setTheme(localStorage.getItem("theme"));
    };

    // Listen for storage changes (if changed from other tabs)
    window.addEventListener("storage", handleStorageChange);

    // Also update on mount
    const interval = setInterval(() => {
      if (localStorage.getItem("theme") !== theme) {
        setTheme(localStorage.getItem("theme"));
      }
    }, 100); // optional fallback, update every 100ms

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      clearInterval(interval);
    };
  }, [theme]);

  useEffect(() => {

    const storedMessage = localStorage.getItem("logoutMessage");
    if (storedMessage) {
      try {
        const notificationData = JSON.parse(storedMessage);
        setNotification(notificationData);
        localStorage.removeItem("logoutMessage");
      } catch (error) {
        // Fallback for old string format
        setNotification({
          message: storedMessage,
          type: "success"
        });
        localStorage.removeItem("logoutMessage");
      }
    }

    const loginMessage = localStorage.getItem("loginMessage");
    if (loginMessage && roles.length > 0) {
      localStorage.removeItem("loginMessage"); // Clear it first
  
      // Redirect based on role
      if (roles.includes("Admin")) {
        navigate("/admin");
      } else if (roles.includes("Manager")) {
        navigate("/manager");
      } else if (roles.includes("User")|| roles.includes("Employee")) {
        navigate("/user/attendance_management");
      } else if (roles.includes("HR")) {
        navigate("/hr");
      } else if (roles.includes("HOD")) {
        navigate("/hod");
      } else {
        navigate("/unauthorized");
      }
    }
  }, [roles, navigate]);
  

  // Handle input field changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value }); // Update state with input values
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoadingLogin(true)
    setError(""); // Clear any previous errors

    try {
      const response = await login(formData); // Attempt login using AuthContext
      localStorage.setItem("loginMessage", JSON.stringify({
        message: "Logged in successfully",
        type: "success"
      }));
      setLoadingLogin(false)
      setJustLoggedIn(true);

    } catch (err) {
      setLoadingLogin(false)
      console.error("Error during login:", err);
      setError(err?.response?.data?.message || "Invalid email or password. Please try again.");
      setNotification({
        message: err?.response?.data?.message || "Invalid email or password. Please try again.",
        type: "error"
      });    
    }
  };

  // forgot password handler
  const handleForgotPassword = async (e) => {
    e.preventDefault();

    try {
      setForgotPasswordLoading(true);

      const response = await axios.post(
        'https://hrmspayroll.prepstripe.com/api/forgot-password',
        { email },
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.status) {
        setNotification({ message: response.data.message || 'Password reset link sent to your email.', type: "success" });
        closeForgotPasswordModal();
      } else {
        setNotification({ message: response.data.message || 'Failed to send password reset link.', type: "error" });
      }
    } catch (error) {
      console.error(error);

      // Check for specific validation errors
      const emailError = error.response?.data?.errors?.email?.[0];

      if (emailError) {
        setNotification({ message: emailError, type: "error" });
      } else {
        setNotification({
          message: "An error occurred while sending the reset link.",
          type: "error",
        });
      }
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col md:flex-row dark:bg-gray-900">

      {/* notification compo */}
      {notification && (
        <Notification message={notification.message} type={notification.type} onClose={() => setNotification(null)} />
      )}

      {/* Left Illustration Panel */}
      <IllustrationPanel/>

      {/* Right Login Panel */}
      <div className="flex flex-col justify-center items-center md:w-1/2 md:mt-0 md:mx-0 mx-2 mt-[122px] md:rounded-none md:shadow-none shadow-lg rounded-2xl md:border-none border-r-4 border-b-4  bg-white dark:bg-gray-900 p-8">
        <div className="w-full max-w-md">
          <div className="text-center mb-6">

            {/* logo */}
            <img
              src={
                theme === "light"
                  ? Logo
                  : theme === "dark"
                  ? Logo_white
                  : Logo 
              }
              className={`w-auto h-16 mx-auto mb-2 ${
                theme === "light"
                  ? "h-[52px]"
                  : theme === "dark"
                  ? "h-[72px]" 
                  : "h-[52px]" 
              } text-center`}
              alt="Logo"
            />
            {/* logo */}
            <h2 className="text-xl font-semibold tracking-tighter dark:text-white">Welcome Back !</h2>
            <p className="text-sm text-gray-500 dark:text-gray-300 mt-1 tracking-tight">Please enter your login credentials</p>
          </div>

          {/* Login Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm text-black dark:text-white font-semibold">
                Email Address
              </label>
              <input
                type="email"
                id="email"
                name="email"
                placeholder="Enter your email"
                value={formData.email}
                onChange={handleInputChange}
                className="w-full dark:border-gray-700 dark:text-white dark:bg-black mt-1 px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#df1f27] placeholder:text-xs"
                required
              />
            </div>
            <div>
              <label htmlFor="password" className="block text-sm text-black dark:text-white font-semibold">
                Password
              </label>
              <input
                type={showPassword ? "text" : "password"}
                id="password"
                name="password"
                placeholder="Enter your password"
                value={formData.password}
                onChange={handleInputChange}
                className="w-full dark:border-gray-700 dark:text-white dark:bg-black mt-1 px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-[#df1f27] placeholder:text-xs"
                required
              />
              {/* eye toggle */}
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute bg-white dark:bg-black -ml-7 mt-[14px] text-gray-500 hover:text-gray-700">
                {showPassword ? (
                  // Eye-off SVG
                  <svg className="w-5 h-5 text-black dark:text-white" data-slot="icon" aria-hidden="true" fill="none" strokeWidth="1.5" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path d="M3.98 8.223A10.477 10.477 0 0 0 1.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.451 10.451 0 0 1 12 4.5c4.756 0 8.773 3.162 10.065 7.498a10.522 10.522 0 0 1-4.293 5.774M6.228 6.228 3 3m3.228 3.228 3.65 3.65m7.894 7.894L21 21m-3.228-3.228-3.65-3.65m0 0a3 3 0 1 0-4.243-4.243m4.242 4.242L9.88 9.88" strokeLinecap="round" strokeLinejoin="round"></path>
                  </svg>
                ) : (
                  // Eye SVG
                  <svg className="w-5 h-5 text-black dark:text-white" data-slot="icon" aria-hidden="true" fill="none" strokeWidth="1.5" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z" strokeLinecap="round" strokeLinejoin="round"></path>
                    <path d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" strokeLinecap="round" strokeLinejoin="round"></path>
                  </svg>
                )}
              </button>
            </div>

            <div className="flex items-center justify-between text-sm">
              <label className="flex items-center space-x-2">
                <input type="checkbox" className="form-checkbox" />
                <span className="dark:text-white">Remember me</span>
              </label>
              <button type="button" onClick={openForgotPasswordModal} className="text-orange-500 hover:underline hover:underline-offset-4">
                Forgot Password?
              </button>
            </div>

            <button
              type="submit"
              disabled={loadingLogin}
              className={`w-full py-2 px-4 rounded-md flex items-center justify-center space-x-2 transition duration-200
                ${loadingLogin 
                  ? 'bg-[#f7d3b7] dark:bg-black dark:border-[#e77a29] dark:text-white cursor-not-allowed text-sm' 
                  : 'bg-[#e77a29] hover:bg-[#833f0f] dark:bg-[#ff8c3a] dark:text-black dark:border-[#e77a29] text-white text-sm'}                
                `}>
              {loadingLogin ? (
                <>
                  <span className="animate-spin rounded-full h-4 w-4 border-t-2 border-white border-opacity-50" />
                  <span>Logging in...</span>
                </>
              ) : (
                <>
                  <span>Login</span>
                  <span>→</span>
                </>
              )}
            </button>
          </form>

        </div>
      </div>
      
      {/* Forgot Password Modal */}
      {isForgotPasswordOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-end md:items-center z-[1001] backdrop-blur-sm">
          <div className="bg-white font-poppins dark:bg-gray-950 dark:border dark:border-gray-700 rounded-t-lg md:rounded-lg shadow-xl p-6 w-full max-w-md mx-auto relative transition-transform duration-300 transform md:animate-none animate-slideUp">
            <h2 className="text-xl font-semibold mb-4 text-gray-800 dark:text-white">Forgot Password</h2>

            <form onSubmit={handleForgotPassword} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Email Address <span className="text-red-500 ml-1">*</span></label>
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full px-3 py-2 border dark:border-gray-700 rounded-md focus:outline-none focus:ring focus:ring-orange-300 dark:bg-black dark:text-white"
                />
              </div>

              <div className="flex justify-between space-x-2 pt-4">
                <button
                  type="button"
                  onClick={closeForgotPasswordModal}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400 dark:bg-gray-700 dark:text-white dark:hover:bg-gray-600"
                  disabled={forgotPasswordLoading}>
                  Cancel
                </button>

                <button
                  type="submit"
                  className={`px-4 py-2 text-black rounded ${
                    forgotPasswordLoading ? 'bg-gray-300' : 'bg-orange-500 hover:bg-orange-600'
                  }`}
                  disabled={forgotPasswordLoading}>
                  {loading ? 'Sending...' : 'Send Reset Link'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Login;
